<svelte:options customElement={{
  tag: 'data-table',
  shadow: 'none',
  props: {
    variant: { reflect: true, type: 'String' },
    headerTitle: { reflect: true, type: 'String' },
    headerTitle2: { reflect: true, type: 'String' },
    headerTitle2Class: { reflect: true, type: 'String' },
    headers: { reflect: true, type: 'String' },
    headerClasses: { reflect: true, type: 'String' },
    rows: { reflect: true, type: 'String' },
    cellClasses: { reflect: true, type: 'String' },
    scrollable: { reflect: true, type: 'Boolean' },
    fixed: { reflect: true, type: 'Boolean' },
    colwidths: { reflect: true, type: 'String' },
    extraClass: { reflect: true, type: 'String' }
  }
}} />

<script>
  /**
   * table：表組み
   * パーツライブラリ準拠の mod-tbl01 / mod-tbl02 / mod-tbl-b 構造
   * @prop {string} variant - 'stack' (mod-tbl01 SPで段落ち) | 'fixed' (mod-tbl02 SP段落ちなし) | 'border-tb' (mod-tbl-b 上下ボーダーのみ)
    * @prop {string} headerTitle - ヘッダー1行目（列結合）に表示するタイトル（任意）
    * @prop {string} headerTitle2 - ヘッダー2行目（列結合）に表示するタイトル（任意）
   * @prop {string} headers - JSON形式のヘッダー '[["見出し1","見出し2"]]'
    * @prop {string} headerClasses - JSON形式のヘッダークラス '[["class-a","class-b"]]'
  * @prop {string} rows - JSON形式のデータ行 '[["data1","data2"],["data3","data4"]]'
  * セルオブジェクト対応: '{"html":"...","colspan":2,"class":"..."}'
    * @prop {string} cellClasses - JSON形式のセルクラス '[["class-a","class-b"],["class-c","class-d"]]'
   * @prop {boolean} scrollable - 横スクロール対応 (mod-tblWrap)
   * @prop {boolean} fixed - テーブルレイアウト固定 (_layout_fixed)
   * @prop {string} colwidths - JSON形式のカラム幅 '["30","70"]' (width-u-*)
  * @prop {string} extraClass - table要素へ追加する任意クラス
   */
  export let variant = 'stack';
  export let headerTitle = '';
  export let headerTitle2 = '';
  export let headerTitle2Class = '';
  export let headers = '[]';
  export let headerClasses = '[]';
  export let rows = '[]';
  export let cellClasses = '[]';
  export let scrollable = false;
  export let fixed = false;
  export let colwidths = '';
  export let extraClass = '';

  let parsedHeaders = [];
  let parsedHeaderClasses = [];
  let parsedRows = [];
  let parsedCellClasses = [];
  let parsedColwidths = [];

  $: {
    try {
      parsedHeaders = typeof headers === 'string' ? JSON.parse(headers) : headers;
    } catch (e) { parsedHeaders = []; }
  }

  $: {
    try {
      parsedHeaderClasses = headerClasses ? (typeof headerClasses === 'string' ? JSON.parse(headerClasses) : headerClasses) : [];
    } catch (e) { parsedHeaderClasses = []; }
  }

  $: {
    try {
      parsedRows = typeof rows === 'string' ? JSON.parse(rows) : rows;
    } catch (e) { parsedRows = []; }
  }

  $: {
    try {
      parsedCellClasses = cellClasses ? (typeof cellClasses === 'string' ? JSON.parse(cellClasses) : cellClasses) : [];
    } catch (e) { parsedCellClasses = []; }
  }

  $: {
    try {
      parsedColwidths = colwidths ? (typeof colwidths === 'string' ? JSON.parse(colwidths) : colwidths) : [];
    } catch (e) { parsedColwidths = []; }
  }

  $: tableClass = [
    variant === 'border-tb' ? 'mod-tbl-b' : variant === 'fixed' ? 'mod-tbl02' : 'mod-tbl01',
    fixed ? '_layout_fixed' : '',
    extraClass
  ].filter(Boolean).join(' ');

  $: headerColCount = parsedHeaders[0]?.length || parsedRows[0]?.length || 1;

  function isCellObject(cell) {
    return cell && typeof cell === 'object' && !Array.isArray(cell);
  }

  function getCellHtml(cell) {
    if (isCellObject(cell)) {
      return cell.html ?? cell.text ?? '';
    }
    return cell ?? '';
  }

  function getCellColspan(cell) {
    if (!isCellObject(cell)) return undefined;
    const span = Number(cell.colspan);
    if (!Number.isFinite(span) || span <= 1) return undefined;
    return Math.floor(span);
  }

  function getCellClass(rowIndex, colIndex, cell) {
    const classFromMatrix = parsedCellClasses[rowIndex]?.[colIndex] || '';
    const classFromCell = isCellObject(cell) ? (cell.class || '') : '';
    return [classFromMatrix, classFromCell].filter(Boolean).join(' ');
  }
</script>

{#if scrollable}
  <div class="mod-tblWrap">
    <table class={tableClass}>
      {#if parsedColwidths.length > 0}
        <colgroup>
          {#each parsedColwidths as w}
            <col class="width-u-{w}" />
          {/each}
        </colgroup>
      {/if}
      {#if parsedHeaders.length > 0}
        <thead>
          {#if headerTitle}
            <tr>
              <th colspan={headerColCount}>{@html headerTitle}</th>
            </tr>
          {/if}
          {#each parsedHeaders as headerRow, headerRowIndex}
            <tr>
              {#each headerRow as cell, headerColIndex}
                <th class={parsedHeaderClasses[headerRowIndex]?.[headerColIndex] || ''}>{@html cell}</th>
              {/each}
            </tr>
          {/each}
          {#if headerTitle2}
            <tr>
              <th colspan={headerColCount} class={headerTitle2Class}>{@html headerTitle2}</th>
            </tr>
          {/if}
        </thead>
      {/if}
      <tbody>
        {#each parsedRows as row, rowIndex}
          <tr>
            {#each row as cell, i}
              {#if i === 0 && variant === 'stack'}
                <th class={getCellClass(rowIndex, i, cell)} colspan={getCellColspan(cell)}>{@html getCellHtml(cell)}</th>
              {:else}
                <td class={getCellClass(rowIndex, i, cell)} colspan={getCellColspan(cell)}>{@html getCellHtml(cell)}</td>
              {/if}
            {/each}
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{:else}
  <table class={tableClass}>
    {#if parsedColwidths.length > 0}
      <colgroup>
        {#each parsedColwidths as w}
          <col class="width-u-{w}" />
        {/each}
      </colgroup>
    {/if}
    {#if parsedHeaders.length > 0}
      <thead>
        {#if headerTitle}
          <tr>
            <th colspan={headerColCount}>{@html headerTitle}</th>
          </tr>
        {/if}
        {#if headerTitle2}
          <tr>
            <th colspan={headerColCount} class={headerTitle2Class}>{@html headerTitle2}</th>
          </tr>
        {/if}
        {#each parsedHeaders as headerRow, headerRowIndex}
          <tr>
            {#each headerRow as cell, headerColIndex}
              <th class={parsedHeaderClasses[headerRowIndex]?.[headerColIndex] || ''}>{@html cell}</th>
            {/each}
          </tr>
        {/each}
      </thead>
    {/if}
    <tbody>
      {#each parsedRows as row, rowIndex}
        <tr>
          {#each row as cell, i}
            {#if i === 0 && variant === 'stack'}
              <th class={getCellClass(rowIndex, i, cell)} colspan={getCellColspan(cell)}>{@html getCellHtml(cell)}</th>
            {:else}
              <td class={getCellClass(rowIndex, i, cell)} colspan={getCellColspan(cell)}>{@html getCellHtml(cell)}</td>
            {/if}
          {/each}
        </tr>
      {/each}
    </tbody>
  </table>
{/if}
